import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Gauge, Smartphone, Monitor, RefreshCw, ExternalLink } from 'lucide-react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/lib/store/authStore'
import { tenantServer } from '@/lib/api/tenantServerClient'

type Strategy = 'mobile' | 'desktop'

interface PageSpeedResult {
  url: string
  strategy: Strategy
  fetched_at?: string
  scores: {
    performance: number | null
    seo: number | null
    accessibility: number | null
    'best-practices': number | null
  }
  metrics: { id: string; title: string; value: string; score: number | null }[]
}

const SCORE_LABELS: { key: keyof PageSpeedResult['scores']; label: string }[] = [
  { key: 'performance', label: 'Performance' },
  { key: 'seo', label: 'SEO' },
  { key: 'accessibility', label: 'Accessibility' },
  { key: 'best-practices', label: 'Best Practices' },
]

/** Lighthouse colour bands (Google's thresholds): ≥90 green, 50–89 orange, <50 red. */
function scoreColor(v: number | null): string {
  if (v == null) return 'text-muted-foreground'
  if (v >= 90) return 'text-emerald-500'
  if (v >= 50) return 'text-amber-500'
  return 'text-red-500'
}
function ringColor(v: number | null): string {
  if (v == null) return '#e5e7eb'
  if (v >= 90) return '#10b981'
  if (v >= 50) return '#f59e0b'
  return '#ef4444'
}

function ScoreRing({ value }: { value: number | null }) {
  const r = 34
  const c = 2 * Math.PI * r
  const pct = value == null ? 0 : value / 100
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" className="shrink-0">
      <circle cx="44" cy="44" r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
      <circle
        cx="44"
        cy="44"
        r={r}
        fill="none"
        stroke={ringColor(value)}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform="rotate(-90 44 44)"
      />
      <text
        x="44"
        y="50"
        textAnchor="middle"
        className={`text-[22px] font-bold ${scoreColor(value)}`}
        fill="currentColor"
      >
        {value == null ? '—' : value}
      </text>
    </svg>
  )
}

export function SiteSpeedPage() {
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl)
  const [strategy, setStrategy] = useState<Strategy>('mobile')

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<PageSpeedResult>({
    queryKey: ['pagespeed', storefrontUrl, strategy],
    queryFn: () =>
      tenantServer.call<PageSpeedResult>('zaviago_backend.google.pagespeed.get_pagespeed', {
        url: storefrontUrl,
        strategy,
      }),
    enabled: !!storefrontUrl,
    staleTime: 60 * 60 * 1000,
    retry: false,
  })

  return (
    <PageContainer>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Site Speed</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            ความเร็วและสุขภาพเว็บไซต์ของคุณ วัดด้วย Google Lighthouse (PageSpeed Insights)
          </p>
        </div>
        {!storefrontUrl ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            ยังไม่มีหน้าร้าน เมื่อคุณเปิดใช้งานหน้าร้านแล้ว คะแนนความเร็วจะแสดงที่นี่โดยอัตโนมัติ
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-border p-0.5">
              {(['mobile', 'desktop'] as Strategy[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStrategy(s)}
                  className={
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
                    (strategy === s
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {s === 'mobile' ? <Smartphone size={15} /> : <Monitor size={15} />}
                  {s === 'mobile' ? 'มือถือ' : 'เดสก์ท็อป'}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <a
                href={storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {storefrontUrl.replace(/^https?:\/\//, '')}
                <ExternalLink size={12} />
              </a>
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                วัดใหม่
              </Button>
            </div>
          </div>

          {/* Scores */}
          <Card>
            <CardContent className="p-6">
              {isLoading || isFetching ? (
                <div className="flex items-center justify-center gap-3 py-10 text-sm text-muted-foreground">
                  <Gauge className="animate-pulse" size={18} /> กำลังวัดความเร็ว… (ใช้เวลา ~10–30 วินาที)
                </div>
              ) : isError ? (
                <div className="py-8 text-center text-sm text-red-500">
                  วัดไม่สำเร็จ: {error instanceof Error ? error.message : 'unknown error'}
                </div>
              ) : data ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {SCORE_LABELS.map(({ key, label }) => (
                    <div key={key} className="flex flex-col items-center gap-2">
                      <ScoreRing value={data.scores[key]} />
                      <span className="text-sm font-medium text-foreground">{label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Core Web Vitals */}
          {data && data.metrics.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="mb-4 text-base font-semibold text-foreground">Core Web Vitals</h3>
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {data.metrics.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-center justify-between rounded-lg border border-border px-4 py-3"
                    >
                      <span className="text-sm text-muted-foreground">{m.title}</span>
                      <span className={`text-sm font-semibold tabular-nums ${scoreColor(
                        m.score == null ? null : Math.round(m.score * 100),
                      )}`}>
                        {m.value}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-muted-foreground">
                  วัดด้วย Google Lighthouse · อัปเดตทุกชั่วโมง · คะแนน ≥90 = ดี, 50–89 = พอใช้, &lt;50 = ควรปรับปรุง
                </p>
              </CardContent>
            </Card>
          )}
        </div>
        )}
      </div>
    </PageContainer>
  )
}
